<script lang="ts">
	import { Stack, Select, Input, Text } from '@Shelly/shelly-design-system';
	import { flowEditorState } from '../../../../stores/flow_editor';
	import { _init_exchange_based_conditions } from '../../../Scenes/helpers/init_exchange_based_conditions';

	export let nodeId: string;

	// Get node data
	$: node = $flowEditorState.nodes.find((n) => n.id === nodeId);
	$: nodeData = node?.data || {};

	// Get exchange markets from helper
	const exchangeConfig = _init_exchange_based_conditions();
	const energyExchangeConfig = exchangeConfig.find((c) => c.cond === 'energy_exchange');

	// Build market options with countries and regions
	const marketOptions: { value: string; title: string }[] = [];
	if (energyExchangeConfig) {
		energyExchangeConfig.countries.forEach((country) => {
			if (country.regions && country.regions.length > 0) {
				country.regions.forEach((region) => {
					marketOptions.push({
						value: `${country.value}|${region.value}`,
						title: `${country.value} - ${region.value}`,
					});
				});
			} else {
				marketOptions.push({
					value: country.value,
					title: country.value.replace(/_/g, ' '),
				});
			}
		});
	}

	// Operators
	const operators = [
		{ value: 'less than', title: 'Less than' },
		{ value: 'more than', title: 'More than' },
	];

	// Current values - make reactive to nodeData changes
	// `exchangeMarket` is the canonical inspector field; `exchange_market` is the legacy spread from SceneToFlow
	$: exchangeMarket = nodeData.exchangeMarket ?? nodeData.exchange_market ?? 'Austria';
	$: operator = nodeData.operator || 'less than';
	$: value = nodeData.value || '';
	$: minutes = nodeData.minutes || 0;

	// Update node when values change
	function updateNode() {
		if (!node) return;

		const updatedData = {
			...nodeData,
			sceneData: undefined,
			originalCondition: undefined,
			condName: 'energy_exchange',
			exchangeMarket,
			operator,
			value: parseFloat(String(value)) || 0,
			minutes,
			label: generateLabel(),
		};

		flowEditorState.updateNode(nodeId, { data: updatedData });
	}

	// Generate label based on settings
	function generateLabel(): string {
		const opLabel = operator === 'less than' ? '<' : '>';
		const marketLabel = exchangeMarket.replace(/\|/g, ' - ').replace(/_/g, ' ');
		return `Energy ${opLabel} ${value} EUR/MWh (${marketLabel})`;
	}
</script>

<div class="energy-inspector">
	<Stack direction="vertical" gap={16}>
		<Text variant="h3">Energy Exchange Price</Text>

		<Select
			id="market-selector"
			icon=""
			aria-label="Exchange Market"
			label="Exchange Market"
			value={exchangeMarket}
			options={marketOptions}
			on:change={(e) => {
				exchangeMarket = e.detail?.value || e.detail;
				updateNode();
			}}
		/>
		<Select
			id="operator-selector"
			icon=""
			aria-label="Condition"
			label="Condition"
			value={operator}
			options={operators}
			on:change={(e) => {
				operator = e.detail?.value || e.detail;
				updateNode();
			}}
		/>
		<Input
			id="price-input"
			aria-label="price-input"
			type="number"
			label="Price (EUR/MWh)"
			{value}
			on:input={(e) => {
				value = e.target.value;
				updateNode();
			}}
			placeholder="e.g., 180"
			min="0"
			step="0.01"
		/>
		<Input
			id="forecast-offset-input"
			aria-label="forecast-offset-input"
			type="number"
			label="Forecast Offset (minutes)"
			value={minutes}
			on:input={(e) => {
				minutes = parseInt(e.target.value) || 0;
				updateNode();
			}}
			placeholder="0"
			min="0"
			max="1440"
		/>

		<div class="info-box">
			<i class="fas fa-info-circle"></i>
			<Stack direction="vertical" gap={8}>
				<Text variant="body2">
					Triggers when energy exchange price meets the specified condition.
				</Text>
				<Text variant="body2">Prices are updated every 15 minutes from spot market data.</Text>
				<Text variant="body2">
					<strong>Business plan required.</strong>
				</Text>
			</Stack>
		</div>
	</Stack>
</div>

<style>
	.energy-inspector {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.info-box {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 12px;
		border-radius: 6px;
		background: rgba(139, 92, 246, 0.1);
		border: 1px solid rgba(139, 92, 246, 0.3);
	}

	.info-box i {
		color: #8b5cf6;
		margin-top: 2px;
		flex-shrink: 0;
	}
</style>
